import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Tabs,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Customer, CustomerChangeRequest } from '../services/customers';
import customerApi from '../services/customerApi';
import { OverviewTab } from '../components/CustomerForm/OverviewTab';
import { EngagementTab } from '../components/CustomerForm/EngagementTab';
import { BusinessTab } from '../components/CustomerForm/BusinessTab';
import { FamilyTab } from '../components/CustomerForm/FamilyTab';
import { AssetsTab } from '../components/CustomerForm/AssetsTab';
import type { UploadFile } from 'antd';
import { Form } from 'antd';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TabPane } = Tabs;

export const CustomerPersonalDetail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [changeRequests, setChangeRequests] = useState<CustomerChangeRequest[]>([]);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [familyPendingChanges, setFamilyPendingChanges] = useState<Record<string, string>>({});
  const [assetsPendingChanges, setAssetsPendingChanges] = useState<Record<string, string>>({});
  const [reportingFiles, setReportingFiles] = useState<{
    identification_passport?: UploadFile[];
    bank?: UploadFile[];
    address?: UploadFile[];
    signature?: UploadFile[];
    tax?: UploadFile[];
  }>({});
  const [removedReportingFiles, setRemovedReportingFiles] = useState<Record<string, boolean>>({});
  const [spouses, setSpouses] = useState<any[]>([]);
  const [children, setChildren] = useState<any[]>([]);
  const [deletedFamilyMembers, setDeletedFamilyMembers] = useState<Array<{ type: 'spouse' | 'children'; index: number; data: any }>>([]);
  const [originalFamilyMembers, setOriginalFamilyMembers] = useState<{ spouses: any[]; children: any[] }>({ spouses: [], children: [] });
  const [planningAssets, setPlanningAssets] = useState<any[]>([]);
  const [existingAssets, setExistingAssets] = useState<any[]>([]);
  const [originalAssets, setOriginalAssets] = useState<{ planning: any[]; existing: any[] }>({ planning: [], existing: [] });
  const [deletedAssets, setDeletedAssets] = useState<Array<{ category: 'planning' | 'existing'; index: number; data: any }>>([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const customerResponse = await customerApi.get('/customer/profile');

      if (customerResponse.data.success) {
        const customerData = customerResponse.data.data;
        setCustomer(customerData);
        
        // Set form values
        const firstContact = customerData.contacts && customerData.contacts.length > 0 ? customerData.contacts[0] : null;
        const firstFinancial = customerData.financials && customerData.financials.length > 0 ? customerData.financials[0] : null;
        
        let engagementValue: string | string[] | undefined = customerData.engagement;
        if (engagementValue && typeof engagementValue === 'string') {
          engagementValue = engagementValue.split(',').map(s => s.trim()).filter(s => s);
        } else if (!engagementValue) {
          engagementValue = [];
        }

        form.setFieldsValue({
          ...customerData,
          engagement: engagementValue,
          contact_number: firstContact?.contact_number || '',
          email: customerData.email || firstContact?.email || '',
          address1: firstContact?.address1 || '',
          address2: firstContact?.address2 || '',
          bank_id: firstFinancial?.bank_id || undefined,
          branch_name: firstFinancial?.branch_name || '',
          bank_number: firstFinancial?.bank_number || '',
          agent_appoint_date: customerData.agent_appoint_date ? dayjs(customerData.agent_appoint_date).startOf('day') : undefined,
          agent_resign_date: customerData.agent_resign_date ? dayjs(customerData.agent_resign_date).startOf('day') : undefined,
          appoint_date: customerData.appoint_date ? dayjs(customerData.appoint_date).startOf('day') : undefined,
          resign_date: customerData.resign_date ? dayjs(customerData.resign_date).startOf('day') : undefined,
          tax_date: customerData.tax_date ? dayjs(customerData.tax_date).startOf('day') : undefined,
        });

        // Load family members
        const familyMembers = customerData.family || [];
        const loadedSpouses = familyMembers.filter((f: any) => f.type === 'spouse');
        const loadedChildren = familyMembers.filter((f: any) => f.type === 'children');
        setSpouses(loadedSpouses);
        setChildren(loadedChildren);
        setOriginalFamilyMembers({ spouses: [...loadedSpouses], children: [...loadedChildren] });
        setDeletedFamilyMembers([]);
        setFamilyPendingChanges({});

        // Load assets
        const assets = customerData.assets || [];
        const planning = assets.filter((a: any) => a.category === 'planning');
        const existing = assets.filter((a: any) => a.category === 'existing');
        setPlanningAssets(planning);
        setExistingAssets(existing);
        setOriginalAssets({ planning: [...planning], existing: [...existing] });
        setDeletedAssets([]);
        setAssetsPendingChanges({});

        // Load change requests
        setChangeRequests(customerData.change_requests || []);

        // Load reporting files
        const reporting = customerData.reporting;
        if (reporting) {
          const fileFields = ['identification_passport', 'bank', 'address', 'signature', 'tax'] as const;
          const files: typeof reportingFiles = {};
          fileFields.forEach(field => {
            const value = (reporting as any)[field];
            if (value) {
              // For supporting documents, we don't need to set a URL
              // The SupportingTab component will use the API endpoint directly
              // Just store a placeholder to indicate file exists
              files[field as keyof typeof files] = [{
                uid: `-${field}-${Date.now()}`,
                name: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                status: 'done',
                // Don't set url - we'll use API endpoint instead
              }];
            }
          });
          setReportingFiles(files);
          setRemovedReportingFiles({});
        }
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      message.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getDisplayValue = (fieldName: string): string => {
    // In edit mode, show pendingChanges
    if (isEditing && pendingChanges[fieldName]) {
      return pendingChanges[fieldName];
    }
    // In view mode, show change request new_value if exists
    const changeRequest = getChangeRequestForField(fieldName);
    if (changeRequest) {
      return changeRequest.new_value;
    }
    return '';
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const getChangeRequestForField = (fieldName: string): CustomerChangeRequest | undefined => {
    return changeRequests.find(cr => cr.field_name === fieldName && cr.status === 'pending');
  };

  const handleApproveChangeRequest = async (requestId: number) => {
    // In customer portal, customers cannot approve their own change requests
    message.info('Change requests must be approved by admin');
  };

  const handleApproveAssetDelete = async (requestId: number) => {
    try {
      await customerApi.put(`/customer/change-requests/${requestId}/approve`);
      message.success('Delete request approved');
      await fetchData();
    } catch {
      message.error('Failed to approve delete request');
    }
  };

  const handleRejectAssetDelete = async (requestId: number) => {
    try {
      await customerApi.put(`/customer/change-requests/${requestId}/reject`);
      message.success('Delete request rejected');
      await fetchData();
    } catch {
      message.error('Failed to reject delete request');
    }
  };

  const handleRejectChangeRequest = async (requestId: number) => {
    // In customer portal, customers cannot reject their own change requests
    message.info('Change requests must be reviewed by admin');
  };

  const handleApprovePendingChange = async (fieldName: string) => {
    // In customer portal, create a change request instead of approving
    try {
      const newValue = pendingChanges[fieldName];
      const oldValue = form.getFieldValue(fieldName as any);
      
      if (!newValue || newValue === oldValue) {
        message.warning('No change to submit');
        return;
      }

      // Create change request with pending status using customerApi
      // customer_id will be automatically set from authenticated customer
      await customerApi.post('/customer/change-requests', {
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'pending',
      });

      // Clear pending change
      const updated = { ...pendingChanges };
      delete updated[fieldName];
      setPendingChanges(updated);

      message.success('Change request submitted successfully');
      
      // Reload customer data to get updated change requests
      await fetchData();
    } catch {
      message.error('Failed to submit change request');
    }
  };

  const handleRejectPendingChange = (fieldName: string) => {
    const updated = { ...pendingChanges };
    delete updated[fieldName];
    setPendingChanges(updated);
    message.info('Change cancelled');
  };

  const handleFileChange = (field: string, files: UploadFile[]) => {
    setReportingFiles((prev) => {
      const prevFiles = prev[field as keyof typeof prev] || [];
      const hadExisting = prevFiles.length > 0 && !prevFiles[0].originFileObj;

      setRemovedReportingFiles((removedPrev) => {
        if (prevFiles.length === 0 && hadExisting) {
          return { ...removedPrev, [field]: true };
        }
        if (prevFiles.length > 0 && removedPrev[field]) {
          const { [field]: _removed, ...rest } = removedPrev;
          return rest;
        }
        return removedPrev;
      });

      return {
        ...prev,
        [field]: files,
      };
    });
  };

  // Family handlers
  const addSpouse = () => {
    setSpouses([...spouses, { type: 'spouse', name: '', identification_no: '' }]);
  };

  const removeSpouse = async (index: number) => {
    const spouseToRemove = spouses[index];
    
    // If the spouse has an ID (exists in database), mark for deletion
    if (spouseToRemove?.id) {
      // Mark as deleted (will be shown with strikethrough)
      setDeletedFamilyMembers(prev => [...prev, { type: 'spouse', index, data: spouseToRemove }]);
      
      // Remove from local state to avoid duplicate display
      setSpouses(spouses.filter((_, i) => i !== index));
      
      // Create change request for deletion (will be submitted on Save)
      message.info('Delete request will be submitted when you click Save');
      return;
    }
    
    // If no ID (new member), just remove from local state
    setSpouses(spouses.filter((_, i) => i !== index));
  };

  const addChild = () => {
    setChildren([...children, { type: 'children', name: '', identification_no: '' }]);
  };

  const removeChild = async (index: number) => {
    const childToRemove = children[index];
    
    // If the child has an ID (exists in database), mark for deletion
    if (childToRemove?.id) {
      // Mark as deleted (will be shown with strikethrough)
      setDeletedFamilyMembers(prev => [...prev, { type: 'children', index, data: childToRemove }]);
      
      // Remove from local state to avoid duplicate display
      setChildren(children.filter((_, i) => i !== index));
      
      // Create change request for deletion (will be submitted on Save)
      message.info('Delete request will be submitted when you click Save');
      return;
    }
    
    // If no ID (new member), just remove from local state
    setChildren(children.filter((_, i) => i !== index));
  };

  // Change family member type (spouse <-> children) in customer portal edit mode
  const handleFamilyTypeChange = (type: 'spouse' | 'children', index: number, newType: 'spouse' | 'children') => {
    if (type === newType) return;

    if (type === 'spouse') {
      const member = spouses[index];
      if (!member) return;
      const newSpouses = spouses.filter((_, i) => i !== index);
      const newChildren = [...children, { ...member, type: 'children' as const }];
      setSpouses(newSpouses);
      setChildren(newChildren);
    } else {
      const member = children[index];
      if (!member) return;
      const newChildren = children.filter((_, i) => i !== index);
      const newSpouses = [...spouses, { ...member, type: 'spouse' as const }];
      setChildren(newChildren);
      setSpouses(newSpouses);
    }

    // Clear pending field changes for this member's previous type/index
    setFamilyPendingChanges(prev => {
      const updated = { ...prev };
      delete updated[`${type}_${index}_name`];
      delete updated[`${type}_${index}_identification_no`];
      return updated;
    });
  };

  const getFamilyDisplayValue = (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no'): string => {
    const key = `${type}_${index}_${field}`;
    return familyPendingChanges[key] || '';
  };

  const handleFamilyFieldChange = (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no', value: string) => {
    const key = `${type}_${index}_${field}`;
    setFamilyPendingChanges(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApproveFamilyChange = async (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no') => {
    // Create change request for family member
    try {
      const key = `${type}_${index}_${field}`;
      const newValue = familyPendingChanges[key];
      
      if (!newValue) {
        message.warning('No change to submit');
        return;
      }

      const familyList = type === 'spouse' ? spouses : children;
      const familyMember = familyList[index];
      const oldValue = familyMember[field] || '';

      const fieldName = `family_${type}_${index}_${field}`;
      await customerApi.post('/customer/change-requests', {
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'pending',
      });

      const updated = { ...familyPendingChanges };
      delete updated[key];
      setFamilyPendingChanges(updated);

      message.success('Change request submitted successfully');
      await fetchData();
    } catch {
      message.error('Failed to submit change request');
    }
  };

  const handleRejectFamilyChange = async (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no') => {
    const key = `${type}_${index}_${field}`;
    const updated = { ...familyPendingChanges };
    delete updated[key];
    setFamilyPendingChanges(updated);
    message.info('Change cancelled');
  };

  // Asset handlers
  const markAssetForDeletion = (category: 'planning' | 'existing', index: number) => {
    const list = category === 'planning' ? planningAssets : existingAssets;
    const assetToRemove = list[index];
    if (!assetToRemove) return;

    if (assetToRemove.id) {
      // Avoid duplicate delete markers for the same asset
      setDeletedAssets(prev => {
        if (prev.some(item => item.data?.id && item.data.id === assetToRemove.id)) {
          return prev;
        }
        return [...prev, { category, index, data: assetToRemove }];
      });

      const updatedList = list.filter((_, i) => i !== index);
      if (category === 'planning') {
        setPlanningAssets(updatedList);
      } else {
        setExistingAssets(updatedList);
      }

      // Drop any pending field changes for the deleted asset index
      setAssetsPendingChanges(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${category}_${index}_`)) {
            delete updated[key];
          }
        });
        return updated;
      });

      message.info('Delete request will be submitted when you click Save');
      return;
    }

    // If the asset is new (no id), just remove it locally
    const updatedList = list.filter((_, i) => i !== index);
    if (category === 'planning') {
      setPlanningAssets(updatedList);
    } else {
      setExistingAssets(updatedList);
    }

    setAssetsPendingChanges(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith(`${category}_${index}_`)) {
          delete updated[key];
        }
      });
      return updated;
    });
  };

  const addPlanningAsset = () => {
    setPlanningAssets([...planningAssets, { category: 'planning', name: '', year: undefined, price: undefined, installment_no: undefined, installment: undefined }]);
  };

  const removePlanningAsset = (index: number) => {
    markAssetForDeletion('planning', index);
  };

  const updatePlanningAsset = (index: number, field: keyof any, value: string | number | undefined) => {
    const updated = [...planningAssets];
    updated[index] = { ...updated[index], [field]: value };
    setPlanningAssets(updated);
  };

  const addExistingAsset = () => {
    setExistingAssets([...existingAssets, { category: 'existing', plate_no: '', model: '', address: '', year: undefined, price: undefined, installment_no: undefined, installment: undefined }]);
  };

  const removeExistingAsset = (index: number) => {
    markAssetForDeletion('existing', index);
  };

  const updateExistingAsset = (index: number, field: keyof any, value: string | number | undefined) => {
    const updated = [...existingAssets];
    updated[index] = { ...updated[index], [field]: value };
    setExistingAssets(updated);
  };

  const getAssetDisplayValue = (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type'): string => {
    const key = `${category}_${index}_${field}`;
    return assetsPendingChanges[key] || '';
  };

  const handleAssetFieldChange = (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type', value: string) => {
    const key = `${category}_${index}_${field}`;
    setAssetsPendingChanges(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApproveAssetChange = async (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => {
    // Create change request for asset
    try {
      const key = `${category}_${index}_${field}`;
      const newValue = assetsPendingChanges[key];
      
      if (!newValue && newValue !== '0') {
        message.warning('No change to submit');
        return;
      }

      const assetList = category === 'planning' ? planningAssets : existingAssets;
      const asset = assetList[index];
      const oldValue = asset[field]?.toString() || '';

      const fieldName = `asset_${category}_${index}_${field}`;
      await customerApi.post('/customer/change-requests', {
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'pending',
      });

      const updated = { ...assetsPendingChanges };
      delete updated[key];
      setAssetsPendingChanges(updated);

      message.success('Change request submitted successfully');
      await fetchData();
    } catch {
      message.error('Failed to submit change request');
    }
  };

  const handleRejectAssetChange = async (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => {
    const key = `${category}_${index}_${field}`;
    const updated = { ...assetsPendingChanges };
    delete updated[key];
    setAssetsPendingChanges(updated);
    message.info('Change cancelled');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const handleCancel = () => {
    setIsEditing(false);
    // Reload customer data to reset form
    fetchData();
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const values = form.getFieldsValue();
      
      // Create change requests for all changed fields
      const changeRequests: Promise<any>[] = [];
      
      // Check each field and create change request if changed
      // Note: engagement, agent_id, team_id are not editable in customer portal
      const fieldsToCheck = [
        'name', 'ic_passport_no', 'tin_no', 'nationality', 'race', 'sex',
        'contact_number', 'email', 'address1', 'address2',
        'bank_id', 'branch_name', 'bank_number', 'password',
        'tax_date'
      ];
      
      for (const fieldName of fieldsToCheck) {
        let newValue = values[fieldName];
        let oldValue = customer?.[fieldName as keyof Customer];
        
        // Handle engagement array
        if (fieldName === 'engagement') {
          if (Array.isArray(newValue)) {
            newValue = newValue.join(',');
          }
          if (Array.isArray(oldValue)) {
            oldValue = oldValue.join(',');
          } else if (typeof oldValue === 'string') {
            oldValue = oldValue;
          }
        }
        
        // Handle date fields
        if (fieldName === 'tax_date' && newValue) {
          if (newValue instanceof Date || typeof newValue === 'string' || typeof newValue === 'number') {
            newValue = dayjs(newValue).format('YYYY-MM-DD');
          }
        }
        if (fieldName === 'tax_date' && oldValue) {
          if (typeof oldValue === 'string') {
            oldValue = oldValue;
          } else if (oldValue instanceof Date || typeof oldValue === 'number') {
            oldValue = dayjs(oldValue).format('YYYY-MM-DD');
          }
        }
        
        // Skip if no change or if field is password and empty
        if (fieldName === 'password' && !newValue) {
          continue;
        }
        
        // Skip if values are the same
        if (newValue === oldValue) {
          continue;
        }
        
        // Skip if newValue is undefined, null, or empty string (unless it's a date or array)
        if (newValue === undefined || newValue === null || (newValue === '' && fieldName !== 'tax_date')) {
          continue;
        }
        
        changeRequests.push(
          customerApi.post('/customer/change-requests', {
            field_name: fieldName,
            old_value: oldValue?.toString() || '',
            new_value: newValue?.toString() || '',
            status: 'pending',
          })
        );
      }
      
      // Handle asset pending changes
      for (const [key, newValue] of Object.entries(assetsPendingChanges)) {
        const parts = key.split('_');
        if (parts.length < 3) continue;
        const category = parts[0] as 'planning' | 'existing';
        const index = parseInt(parts[1], 10);
        const field = parts.slice(2).join('_') as 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type';
        const oldList = category === 'planning' ? originalAssets.planning : originalAssets.existing;
        const oldAsset = oldList[index];
        // If the asset does not exist in original list (new row), skip field-level change requests;
        // it will be covered by the corresponding asset_{category}_{index}_add request instead.
        if (!oldAsset || !oldAsset.id) {
          continue;
        }
        const oldValue = oldAsset[field]?.toString() || '';
        const fieldName = `asset_${category}_${index}_${field}`;
        changeRequests.push(
          customerApi.post('/customer/change-requests', {
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue,
            status: 'pending',
          })
        );
      }

      // Handle family member field changes (name, identification_no)
      // Build a mapping from actualIndex (in type-specific array) to originalIndex
      // This is needed because handleFamilyFieldChange uses actualIndex, but we need originalIndex to find the original value
      const familyActualIndexToOriginalIndexMap = new Map<string, number>();
      
      // Process spouses
      spouses.forEach((spouse, actualIndex) => {
        const spouseId = (spouse as any).id;
        // Skip if spouse is marked for deletion
        const deletedSpouseIds = new Set(deletedFamilyMembers.filter(d => d.type === 'spouse').map(d => d.data?.id).filter(id => id !== undefined && id !== null));
        if (spouseId && deletedSpouseIds.has(spouseId)) {
          return;
        }
        // Calculate originalIndex: actualIndex + number of deleted items before this position
        let originalIndex = actualIndex;
        deletedFamilyMembers.forEach(deleted => {
          if (deleted.type === 'spouse' && deleted.index <= actualIndex) {
            originalIndex++;
          }
        });
        familyActualIndexToOriginalIndexMap.set(`spouse_${actualIndex}`, originalIndex);
      });
      
      // Process children
      children.forEach((child, actualIndex) => {
        const childId = (child as any).id;
        // Skip if child is marked for deletion
        const deletedChildIds = new Set(deletedFamilyMembers.filter(d => d.type === 'children').map(d => d.data?.id).filter(id => id !== undefined && id !== null));
        if (childId && deletedChildIds.has(childId)) {
          return;
        }
        // Calculate originalIndex: actualIndex + number of deleted items before this position
        let originalIndex = actualIndex;
        deletedFamilyMembers.forEach(deleted => {
          if (deleted.type === 'children' && deleted.index <= actualIndex) {
            originalIndex++;
          }
        });
        familyActualIndexToOriginalIndexMap.set(`children_${actualIndex}`, originalIndex);
      });

      for (const [key, newValue] of Object.entries(familyPendingChanges)) {
        const parts = key.split('_');
        if (parts.length >= 3) {
          const type = parts[0] as 'spouse' | 'children';
          const actualIndex = parseInt(parts[1], 10);
          const field = parts.slice(2).join('_') as 'name' | 'identification_no';
          
          if (type === 'spouse' || type === 'children') {
            const familyList = type === 'spouse' ? spouses : children;
            const familyMember = familyList[actualIndex];
            
            if (familyMember) {
              // For new members (no id), skip field-level change requests; the add request will carry the data
              if (!(familyMember as any).id) {
                continue;
              }
              
              // Convert actualIndex to originalIndex using the mapping
              const originalIndex = familyActualIndexToOriginalIndexMap.get(`${type}_${actualIndex}`);
              if (originalIndex === undefined) {
                // Skip if we can't find the mapping (shouldn't happen, but be safe)
                continue;
              }
              
              // Get original family member from original data
              const originalFamilyList = type === 'spouse' ? originalFamilyMembers.spouses : originalFamilyMembers.children;
              const oldMember = originalFamilyList[originalIndex];
              if (!oldMember || !oldMember.id) {
                // If this member is new (no original entry with id), skip field-level change requests;
                // it will be represented by the family_{type}_{index}_add request.
                continue;
              }
              
              const oldValue = oldMember[field] || '';
              const fieldName = `family_${type}_${originalIndex}_${field}`; // Use originalIndex for field name
              
              // Only create change request if value actually changed
              if (oldValue !== newValue) {
                changeRequests.push(
                  customerApi.post('/customer/change-requests', {
                    field_name: fieldName,
                    old_value: oldValue || '',
                    new_value: newValue || '',
                    status: 'pending',
                  })
                );
              }
            }
          }
        }
      }
      
      // Handle new family members (only those without ID)
      // Exclude members that are marked for deletion
      const deletedMemberIds = new Set(deletedFamilyMembers.map(d => d.data?.id).filter(id => id));
      
      // Process spouses
      spouses.forEach((spouse, actualIndex) => {
        const spouseId = (spouse as any).id;
        // Skip if spouse is marked for deletion
        if (deletedMemberIds.has(spouseId)) {
          return;
        }
        // Only create change request for truly new spouses (no ID)
        if (!spouseId) {
          // Calculate originalIndex: actualIndex + number of deleted items before this position
          let originalIndex = actualIndex;
          deletedFamilyMembers.forEach(deleted => {
            if (deleted.type === 'spouse' && deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          
          // Merge pending inputs for this spouse so request carries entered data
          const pendingNameKey = `spouse_${actualIndex}_name`;
          const pendingIcKey = `spouse_${actualIndex}_identification_no`;
          const mergedMember = {
            ...spouse,
            type: 'spouse',
            name: familyPendingChanges[pendingNameKey] ?? spouse.name ?? '',
            identification_no: familyPendingChanges[pendingIcKey] ?? spouse.identification_no ?? '',
          };

          // Only create request if at least name has a value (non-empty)
          if (mergedMember.name.trim()) {
            const fieldName = `family_spouse_${originalIndex}_add`; // Use originalIndex
            changeRequests.push(
              customerApi.post('/customer/change-requests', {
                field_name: fieldName,
                old_value: '',
                new_value: JSON.stringify(mergedMember),
                status: 'pending',
              })
            );
          }
        }
      });
      
      // Process children
      children.forEach((child, actualIndex) => {
        const childId = (child as any).id;
        // Skip if child is marked for deletion
        if (deletedMemberIds.has(childId)) {
          return;
        }
        // Only create change request for truly new children (no ID)
        if (!childId) {
          // Calculate originalIndex: actualIndex + number of deleted items before this position
          let originalIndex = actualIndex;
          deletedFamilyMembers.forEach(deleted => {
            if (deleted.type === 'children' && deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          
          // Merge pending inputs for this child so request carries entered data
          const pendingNameKey = `children_${actualIndex}_name`;
          const pendingIcKey = `children_${actualIndex}_identification_no`;
          const mergedMember = {
            ...child,
            type: 'children',
            name: familyPendingChanges[pendingNameKey] ?? child.name ?? '',
            identification_no: familyPendingChanges[pendingIcKey] ?? child.identification_no ?? '',
          };

          // Only create request if at least name has a value (non-empty)
          if (mergedMember.name.trim()) {
            const fieldName = `family_children_${originalIndex}_add`; // Use originalIndex
            changeRequests.push(
              customerApi.post('/customer/change-requests', {
                field_name: fieldName,
                old_value: '',
                new_value: JSON.stringify(mergedMember),
                status: 'pending',
              })
            );
          }
        }
      });
      
      // Handle deleted family members (create change requests for deletion on Save)
      for (const deleted of deletedFamilyMembers) {
        if (deleted.data?.id) {
          const fieldName = `family_${deleted.type}_${deleted.index}_delete`;
          changeRequests.push(
            customerApi.post('/customer/change-requests', {
              field_name: fieldName,
              old_value: JSON.stringify(deleted.data),
              new_value: null, // null for delete operations
              status: 'pending',
            })
          );
        }
      }

      // Handle asset pending changes
      // Build a mapping from actualIndex (in category-specific array) to originalIndex
      // This is needed because handleAssetFieldChange uses actualIndex, but we need originalIndex to find the original value
      const assetActualIndexToOriginalIndexMap = new Map<string, number>();
      
      // Process existing assets
      existingAssets.forEach((asset, actualIndex) => {
        const assetId = (asset as any).id;
        // Skip if asset is marked for deletion
        const deletedExistingIds = new Set(deletedAssets.filter(d => d.category === 'existing').map(d => d.data?.id).filter(id => id !== undefined && id !== null));
        if (assetId && deletedExistingIds.has(assetId)) {
          return;
        }
        // Calculate originalIndex: actualIndex + number of deleted items before this position
        let originalIndex = actualIndex;
        deletedAssets.forEach(deleted => {
          if (deleted.category === 'existing' && deleted.index <= actualIndex) {
            originalIndex++;
          }
        });
        assetActualIndexToOriginalIndexMap.set(`existing_${actualIndex}`, originalIndex);
      });
      
      // Process planning assets
      planningAssets.forEach((asset, actualIndex) => {
        const assetId = (asset as any).id;
        // Skip if asset is marked for deletion
        const deletedPlanningIds = new Set(deletedAssets.filter(d => d.category === 'planning').map(d => d.data?.id).filter(id => id !== undefined && id !== null));
        if (assetId && deletedPlanningIds.has(assetId)) {
          return;
        }
        // Calculate originalIndex: actualIndex + number of deleted items before this position
        let originalIndex = actualIndex;
        deletedAssets.forEach(deleted => {
          if (deleted.category === 'planning' && deleted.index <= actualIndex) {
            originalIndex++;
          }
        });
        assetActualIndexToOriginalIndexMap.set(`planning_${actualIndex}`, originalIndex);
      });

      for (const [key, newValue] of Object.entries(assetsPendingChanges)) {
        const parts = key.split('_');
        if (parts.length < 3) continue;
        const category = parts[0] as 'planning' | 'existing';
        const actualIndex = parseInt(parts[1], 10);
        const field = parts.slice(2).join('_') as 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type';
        
        const assetList = category === 'planning' ? planningAssets : existingAssets;
        const asset = assetList[actualIndex];
        
        if (asset) {
          // For new assets (no id), skip field-level change requests; the add request will carry the data
          if (!(asset as any).id) {
            continue;
          }
          
          // Convert actualIndex to originalIndex using the mapping
          const originalIndex = assetActualIndexToOriginalIndexMap.get(`${category}_${actualIndex}`);
          if (originalIndex === undefined) {
            // Skip if we can't find the mapping (shouldn't happen, but be safe)
            continue;
          }
          
          // Get original asset from original data
          const originalAssetList = category === 'planning' ? originalAssets.planning : originalAssets.existing;
          const oldAsset = originalAssetList[originalIndex];
          if (!oldAsset || !oldAsset.id) {
            // If this asset is new (no original entry with id), skip field-level change requests;
            // it will be represented by the asset_{category}_{index}_add request.
            continue;
          }
          
          const oldValue = oldAsset[field]?.toString() || '';
          const fieldName = `asset_${category}_${originalIndex}_${field}`; // Use originalIndex for field name
          
          // Only create change request if value actually changed
          if (oldValue !== newValue) {
        changeRequests.push(
          customerApi.post('/customer/change-requests', {
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue,
            status: 'pending',
          })
        );
          }
        }
      }

      // Handle new assets (no id) -> add requests
      // Process existing assets
      existingAssets.forEach((asset, actualIndex) => {
        const assetId = (asset as any).id;
        // Skip if asset is marked for deletion
        const deletedAssetIds = new Set(deletedAssets.map(d => d.data?.id).filter(id => id));
        if (deletedAssetIds.has(assetId)) {
          return;
        }
        // Only create change request for truly new assets (no ID)
        if (!assetId) {
          // Calculate originalIndex: actualIndex + number of deleted items before this position
          let originalIndex = actualIndex;
          deletedAssets.forEach(deleted => {
            if (deleted.category === 'existing' && deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          
          // Merge pending inputs for this asset so request carries entered data
          const mergedAsset: any = { ...asset, category: 'existing' };
          ['type', 'plate_no', 'model', 'address', 'year', 'price', 'installment'].forEach(field => {
            const pendingKey = `existing_${actualIndex}_${field}`;
            if (assetsPendingChanges[pendingKey] !== undefined) {
              if (field === 'year') {
                mergedAsset[field] = parseInt(assetsPendingChanges[pendingKey], 10);
              } else if (field === 'price' || field === 'installment') {
                mergedAsset[field] = parseFloat(assetsPendingChanges[pendingKey]);
              } else {
                mergedAsset[field] = assetsPendingChanges[pendingKey];
              }
            }
          });

          // Only create request if at least type has a value (non-empty)
          if (mergedAsset.type) {
            const fieldName = `asset_existing_${originalIndex}_add`; // Use originalIndex
            changeRequests.push(
              customerApi.post('/customer/change-requests', {
                field_name: fieldName,
                old_value: '',
                new_value: JSON.stringify(mergedAsset),
                status: 'pending',
              })
            );
          }
        }
      });
      
      // Process planning assets
      planningAssets.forEach((asset, actualIndex) => {
        const assetId = (asset as any).id;
        // Skip if asset is marked for deletion
        const deletedAssetIds = new Set(deletedAssets.map(d => d.data?.id).filter(id => id));
        if (deletedAssetIds.has(assetId)) {
          return;
        }
        // Only create change request for truly new assets (no ID)
        if (!assetId) {
          // Calculate originalIndex: actualIndex + number of deleted items before this position
          let originalIndex = actualIndex;
          deletedAssets.forEach(deleted => {
            if (deleted.category === 'planning' && deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          
          // Merge pending inputs for this asset so request carries entered data
          const mergedAsset: any = { ...asset, category: 'planning' };
          ['type', 'plate_no', 'model', 'address', 'year', 'price', 'installment'].forEach(field => {
            const pendingKey = `planning_${actualIndex}_${field}`;
            if (assetsPendingChanges[pendingKey] !== undefined) {
              if (field === 'year') {
                mergedAsset[field] = parseInt(assetsPendingChanges[pendingKey], 10);
              } else if (field === 'price' || field === 'installment') {
                mergedAsset[field] = parseFloat(assetsPendingChanges[pendingKey]);
              } else {
                mergedAsset[field] = assetsPendingChanges[pendingKey];
              }
            }
          });

          // Only create request if at least type has a value (non-empty)
          if (mergedAsset.type) {
            const fieldName = `asset_planning_${originalIndex}_add`; // Use originalIndex
            changeRequests.push(
              customerApi.post('/customer/change-requests', {
                field_name: fieldName,
                old_value: '',
                new_value: JSON.stringify(mergedAsset),
                status: 'pending',
              })
            );
          }
        }
      });

      // Handle deleted assets
      deletedAssets.forEach((item: { category: 'planning' | 'existing'; index: number; data: any }) => {
        if (item.data?.id) {
          const fieldName = `asset_${item.category}_${item.index}_delete`;
          changeRequests.push(
            customerApi.post('/customer/change-requests', {
              field_name: fieldName,
              old_value: JSON.stringify(item.data),
              new_value: null,
              status: 'pending',
            })
          );
        }
      });

      // Handle signature file upload
      const signatureFile = reportingFiles.signature?.[0];
      const signatureRemoved = !!removedReportingFiles.signature;
      if (signatureFile && signatureFile.originFileObj) {
        // Upload signature file directly to customer profile update endpoint
        const formData = new FormData();
        formData.append('signature', signatureFile.originFileObj);
        
        try {
          await customerApi.put('/customer/profile', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (error) {
          console.error('Failed to upload signature:', error);
          message.error('Failed to upload signature file');
        }
      }
      if (signatureRemoved) {
        const formData = new FormData();
        formData.append('signature', '');
        try {
          await customerApi.put('/customer/profile', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (error) {
          console.error('Failed to delete signature:', error);
          message.error('Failed to delete signature');
        }
      }

      if (changeRequests.length > 0) {
        await Promise.all(changeRequests);
        message.success(`${changeRequests.length} change request(s) submitted successfully`);
        
        // Clear pending changes after successful submission
        setFamilyPendingChanges({});
        setDeletedFamilyMembers([]);
        setAssetsPendingChanges({});
        setDeletedAssets([]);
        
        setIsEditing(false);
        await fetchData();
        
        // Keep current tab in URL after save
        setSearchParams({ tab: activeTab });
      } else if (signatureFile && signatureFile.originFileObj) {
        // Only signature file uploaded, no other changes
        message.success('Signature updated successfully');
        setIsEditing(false);
        await fetchData();
        setSearchParams({ tab: activeTab });
      } else if (signatureRemoved) {
        message.success('Signature deleted successfully');
        setIsEditing(false);
        await fetchData();
        setSearchParams({ tab: activeTab });
      } else {
        message.info('No changes to submit');
        setIsEditing(false);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to submit change requests');
    } finally {
      setSubmitting(false);
    }
  };

  if (!customer) {
    return <div>Customer not found</div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer/personal')}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {customer.name}
          </Title>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      </div>

      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Overview" key="overview">
            <OverviewTab
              form={form}
              isEdit={true}
              isEditing={isEditing}
              banks={[]}
              getDisplayValue={getDisplayValue}
              handleFieldChange={handleFieldChange}
              getChangeRequestForField={getChangeRequestForField}
              handleApproveChangeRequest={handleApproveChangeRequest}
              handleRejectChangeRequest={handleRejectChangeRequest}
              handleApprovePendingChange={handleApprovePendingChange}
              handleRejectPendingChange={handleRejectPendingChange}
              isCustomerPortal={true}
              reportingFiles={reportingFiles}
              onFileChange={handleFileChange}
              customerId={customer?.id}
            />
          </TabPane>

          <TabPane tab="Engagement" key="engagement">
            <EngagementTab
              form={form}
              staff={[]}
              isEditing={isEditing}
              isCustomerPortal={true}
              team={customer?.team || null}
              reportingFiles={reportingFiles}
              onFileChange={handleFileChange}
              customerId={customer?.id}
            />
          </TabPane>

          <TabPane tab="Business" key="business">
            <BusinessTab isCustomerPortal={true} />
          </TabPane>

          <TabPane tab="Family" key="family">
            <FamilyTab
              isEdit={true}
              isEditing={isEditing}
              canEdit={true}
              canDelete={true}
              isCustomerPortal={true}
              spouses={spouses}
              children={children}
              deletedFamilyMembers={deletedFamilyMembers}
              changeRequests={changeRequests.map(cr => ({
                field_name: cr.field_name,
                status: cr.status,
                old_value: cr.old_value || '',
                new_value: cr.new_value || '',
                id: cr.id,
              }))}
              addSpouse={addSpouse}
              removeSpouse={removeSpouse}
              addChild={addChild}
              removeChild={removeChild}
              handleFamilyTypeChange={handleFamilyTypeChange}
              getFamilyDisplayValue={getFamilyDisplayValue}
              handleFamilyFieldChange={handleFamilyFieldChange}
            />
          </TabPane>

          <TabPane tab="Assets" key="assets">
            <AssetsTab
              isEdit={true}
              isEditing={isEditing}
              isCustomerPortal={true}
              existingAssets={existingAssets}
              planningAssets={planningAssets}
              addExistingAsset={addExistingAsset}
              removeExistingAsset={removeExistingAsset}
              updateExistingAsset={updateExistingAsset}
              addPlanningAsset={addPlanningAsset}
              removePlanningAsset={removePlanningAsset}
              updatePlanningAsset={updatePlanningAsset}
              getAssetDisplayValue={getAssetDisplayValue}
              handleAssetFieldChange={handleAssetFieldChange}
              handleApproveAssetChange={handleApproveAssetChange}
              handleRejectAssetChange={handleRejectAssetChange}
              deletedAssets={deletedAssets}
              changeRequests={changeRequests.map(cr => ({
                field_name: cr.field_name,
                status: cr.status,
                old_value: cr.old_value || '',
                new_value: cr.new_value || '',
                id: cr.id,
              }))}
              handleApproveAssetRequests={undefined}
              handleRejectAssetRequests={undefined}
              handleApproveAssetDelete={handleApproveAssetDelete}
              handleRejectAssetDelete={handleRejectAssetDelete}
              handleApproveChangeRequest={handleApproveChangeRequest}
              handleRejectChangeRequest={handleRejectChangeRequest}
            />
          </TabPane>

        </Tabs>
      </Form>

      {isEditing && (
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24
        }}>
          <Space>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={handleSave}
              loading={submitting}
            >
              Save
            </Button>
          </Space>
        </div>
      )}
    </Card>
  );
};
